import { toast } from "sonner";
import { columnaDe, ETIQUETA_COLUMNA, etapaDeColumna, primerNombre } from "@/reclutamiento/logica";
import type { CandidatoPortal, Columna } from "@/reclutamiento/tipos";
import type { PortalApi } from "./api";
import { AVISO_SOLO_LECTURA } from "./contexto";
import { guardarCambio } from "./pendientes";
import { olvidarFicha } from "./precarga";
import { mensajeError } from "./util";

type Aplicar<T> = (id: string, f: (x: T) => T) => void;

function puedeEscribir(k: CandidatoPortal, soloLectura: boolean): boolean {
  if (soloLectura) {
    toast.error(AVISO_SOLO_LECTURA);
    return false;
  }
  if (k.bloqueado) {
    toast.error("Las fichas se liberan al confirmar tu pago.");
    return false;
  }
  return true;
}

/**
 * Mueve con actualización optimista: pinta el cambio, llama a la API y, si falla, regresa la tarjeta y avisa.
 * `aplicar` recibe una función que transforma al candidato (para listas o para la ficha).
 * Descartar NO se hace aquí: pide motivo (`pedirDescarte`) y luego va por `descartarCandidato`.
 */
export async function moverCandidato<T extends CandidatoPortal>(
  api: PortalApi,
  k: T,
  col: Columna,
  aplicar: Aplicar<T>,
  soloLectura: boolean,
  pedirDescarte: (k: T) => void,
) {
  if (!puedeEscribir(k, soloLectura)) return;
  if (col === "descartado") {
    pedirDescarte(k);
    return;
  }
  if (k.columna === col) return;
  const etapa = etapaDeColumna(col);
  if (etapa === "descartado") return; // (no pasa: la columna descartado se atendió arriba)
  const nueva = columnaDe(k.veredicto, etapa);
  const antes = { etapa: k.etapa, columna: k.columna, motivo_cliente: k.motivo_cliente };
  aplicar(k.id, (x) => ({ ...x, etapa, columna: nueva, motivo_cliente: null }));
  try {
    const quien = primerNombre(k.nombre) || "El candidato";
    // Sin conexión no se regresa la tarjeta: el cambio se queda y se manda solo al reconectar.
    if ((await guardarCambio(api, { tipo: "etapa", id: k.id, nombre: k.nombre, etapa })) === "en-cola")
      return void toast.info(`Sin conexión: ${quien} ya está en ${ETIQUETA_COLUMNA[nueva]} y se guardará al reconectar.`);
    if (nueva === col) toast.success(`${quien} pasó a ${ETIQUETA_COLUMNA[nueva]}`);
    else toast.info(`${quien} queda en ${ETIQUETA_COLUMNA[nueva]}: así lo dejó el filtro de la vacante.`);
  } catch (e) {
    aplicar(k.id, (x) => ({ ...x, ...antes }));
    toast.error(mensajeError(e, "No se pudo mover. Intenta de nuevo."));
  }
}

/** Descarta con motivo (optimista) y ofrece «Deshacer» en el aviso. */
export async function descartarCandidato<T extends CandidatoPortal>(
  api: PortalApi,
  k: T,
  motivo: string,
  aplicar: Aplicar<T>,
  soloLectura: boolean,
) {
  if (!puedeEscribir(k, soloLectura)) return;
  const antes = { etapa: k.etapa, columna: k.columna, motivo_cliente: k.motivo_cliente };
  aplicar(k.id, (x) => ({ ...x, etapa: "descartado", columna: "descartado", motivo_cliente: motivo }));
  try {
    const quien = primerNombre(k.nombre) || "El candidato";
    if ((await guardarCambio(api, { tipo: "descartar", id: k.id, nombre: k.nombre, motivo })) === "en-cola")
      return void toast.info(`Sin conexión: ${quien} ya está en Descartados y se guardará al reconectar.`);
    toast.success(`${quien} pasó a Descartados: ${motivo}`, {
      action: { label: "Deshacer", onClick: () => void regresarCandidato(api, { ...k, etapa: "descartado" }, aplicar, soloLectura) },
    });
  } catch (e) {
    aplicar(k.id, (x) => ({ ...x, ...antes }));
    toast.error(mensajeError(e, "No se pudo descartar. Intenta de nuevo."));
  }
}

/** «Regresar a activos»: deshace el descarte del cliente. */
export async function regresarCandidato<T extends CandidatoPortal>(
  api: PortalApi,
  k: T,
  aplicar: Aplicar<T>,
  soloLectura: boolean,
) {
  if (!puedeEscribir(k, soloLectura)) return;
  try {
    olvidarFicha(k.id);
    const etapa = await api.regresarAActivos(k.id);
    const columna = columnaDe(k.veredicto, etapa);
    aplicar(k.id, (x) => ({ ...x, etapa, columna, motivo_cliente: null }));
    const quien = primerNombre(k.nombre) || "El candidato";
    if (columna === "descartado") toast.info(`${quien} ya no está descartado por ti, pero sigue en Descartados por el filtro de la vacante.`);
    else toast.success(`${quien} regresó a ${ETIQUETA_COLUMNA[columna]}`);
  } catch (e) {
    toast.error(mensajeError(e, "No se pudo regresar. Intenta de nuevo."));
  }
}
